import React, { useState } from 'react';
import { FileX2, Download, Copy, RefreshCw, Loader2, CheckCircle2, Clock, AlertTriangle, X, Lock } from 'lucide-react';
import type { DeclaracaoNaoAssociacao } from '../../../../types';
import type { Declaracao } from '../../../../supabase/functions/_shared/declaracaoNaoAssociacao.ts';

/**
 * Card "Declaração de não associação" — só interface, sem Supabase nem sessão,
 * para dar para pré-visualizar fora do login (.superpowers/declaracao-nao-associacao).
 * Quem liga ao app é o DeclaracaoNaoAssociacaoPanel.
 */
export interface DeclaracaoNaoAssociacaoCardProps {
    /** A mais recente primeiro — é a que vale. */
    declaracoes: DeclaracaoNaoAssociacao[];
    podeEmitir: boolean;
    /** Motivo, já conhecido na tela, para não deixar emitir (ex.: avalista em andamento). */
    bloqueio: string | null;
    /** Texto que vai no PDF, montado com os dados atuais da negociação. */
    previa: Declaracao;
    procuradorNome: string;
    acao: 'emitir' | 'atualizar' | null;
    /** Devolve se emitiu: em falha a confirmação continua aberta. */
    onEmitir: () => Promise<boolean>;
    onAtualizar: (declaracao: DeclaracaoNaoAssociacao) => void;
    onCopiarLink: (url: string) => void;
}

const dataBr = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');

const DeclaracaoNaoAssociacaoCard: React.FC<DeclaracaoNaoAssociacaoCardProps> = ({
    declaracoes, podeEmitir, bloqueio, previa, procuradorNome, acao, onEmitir, onAtualizar, onCopiarLink,
}) => {
    const [confirmando, setConfirmando] = useState(false);
    const atual = declaracoes[0] ?? null;
    const anteriores = declaracoes.slice(1);
    const reemissao = !!atual;

    const confirmar = async () => {
        if (await onEmitir()) setConfirmando(false);
    };

    return (
        <>
            <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <FileX2 size={13} className="text-slate-600 dark:text-slate-300" />
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
                            Declaração de não associação
                        </span>
                    </div>
                    {atual && (
                        atual.assinado_em ? (
                            <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
                                <CheckCircle2 size={12} /> Assinada
                            </span>
                        ) : (
                            <span className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-400 whitespace-nowrap" title="Aguardando a assinatura do procurador">
                                <Clock size={12} /> Pendente
                            </span>
                        )
                    )}
                </div>

                {!atual && (
                    <>
                        <p className="text-xs text-slate-600 dark:text-slate-300">
                            Atestado para o cliente de que ele não é associado. Sai assinado por {procuradorNome} na hora; o cliente não assina.
                        </p>
                        {podeEmitir ? (
                            <>
                                <button
                                    onClick={() => setConfirmando(true)}
                                    disabled={!!bloqueio || acao !== null}
                                    className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-200 hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                                >
                                    <FileX2 size={13} />
                                    Emitir declaração
                                </button>
                                {bloqueio && <p className="text-[11px] text-amber-700 dark:text-amber-400">{bloqueio}</p>}
                            </>
                        ) : (
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                Quem emite: adm, gerente, analista ou o vendedor responsável pela negociação.
                            </p>
                        )}
                    </>
                )}

                {atual && (
                    <div className="space-y-2">
                        <p className="text-xs text-slate-600 dark:text-slate-300">
                            Emitida em {dataBr(atual.created_at)}
                            {atual.emissor?.full_name && <> por {atual.emissor.full_name}</>}
                            {atual.assinado_em
                                ? <> · assinada por {atual.procurador_nome} em {dataBr(atual.assinado_em)}.</>
                                : <> · falta a assinatura de {atual.procurador_nome}, que também recebe o pedido por e-mail do Autentique.</>}
                        </p>

                        {atual.assinado_em && atual.url_pdf_assinado ? (
                            <div className="flex flex-wrap gap-2">
                                <a
                                    href={atual.url_pdf_assinado}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                                >
                                    <Download size={12} /> Baixar PDF
                                </a>
                                <button
                                    type="button"
                                    onClick={() => onCopiarLink(atual.url_pdf_assinado!)}
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                                >
                                    <Copy size={12} /> Copiar link
                                </button>
                            </div>
                        ) : podeEmitir && (
                            <button
                                onClick={() => onAtualizar(atual)}
                                disabled={acao !== null}
                                className="flex items-center gap-1.5 text-xs font-bold text-amber-700 dark:text-amber-400 hover:underline disabled:opacity-60"
                            >
                                {acao === 'atualizar' ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                                Verificar de novo
                            </button>
                        )}

                        <p className="flex items-start gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                            <Lock size={11} className="mt-0.5 shrink-0" />
                            Negociação encerrada: o avalista e o reenvio para validação de dados estão bloqueados. Se o cliente voltar, abra uma negociação nova.
                        </p>

                        {anteriores.length > 0 && (
                            <div className="pt-1 border-t border-slate-200 dark:border-slate-700 space-y-0.5">
                                <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Emissões anteriores</p>
                                {anteriores.map(d => (
                                    <p key={d.id} className="text-[11px] text-slate-500 dark:text-slate-400">
                                        {dataBr(d.created_at)} · {d.cliente_nome}
                                        {d.url_pdf_assinado && (
                                            <> · <a href={d.url_pdf_assinado} target="_blank" rel="noopener noreferrer" className="underline">PDF</a></>
                                        )}
                                    </p>
                                ))}
                            </div>
                        )}

                        {podeEmitir && (
                            <button
                                onClick={() => setConfirmando(true)}
                                disabled={acao !== null}
                                className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:underline disabled:opacity-60"
                            >
                                Emitir novamente (nome ou CPF/CNPJ estava errado)
                            </button>
                        )}
                    </div>
                )}
            </div>

            {confirmando && (
                <div
                    className="fixed inset-0 z-[100010] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
                    onClick={() => acao === null && setConfirmando(false)}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="titulo-declaracao"
                        className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-start gap-3 px-5 py-4 border-b border-gray-100 dark:border-slate-800">
                            <div className="flex-1">
                                <h3 id="titulo-declaracao" className="text-base font-bold text-gray-900 dark:text-white">
                                    {reemissao ? 'Emitir nova declaração' : 'Emitir declaração de não associação'}
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                    Sai assinada por {procuradorNome} na hora. Confira o texto abaixo: é exatamente o que vai no PDF.
                                </p>
                            </div>
                            <button
                                onClick={() => setConfirmando(false)}
                                disabled={acao !== null}
                                className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"
                                title="Fechar"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="overflow-y-auto custom-scrollbar px-5 py-4 space-y-4">
                            <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 p-4 space-y-3 text-[13px] leading-relaxed text-gray-800 dark:text-slate-200">
                                <p className="text-center text-xs font-bold tracking-wide">{previa.titulo}</p>
                                {previa.paragrafos.map((trechos, i) => (
                                    <p key={i} className="text-justify">
                                        {trechos.map((t, j) => (t.negrito ? <strong key={j}>{t.texto}</strong> : <React.Fragment key={j}>{t.texto}</React.Fragment>))}
                                    </p>
                                ))}
                                <p>{previa.localData}</p>
                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                    {previa.assinatura.linha1}<br />{previa.assinatura.linha2}
                                </p>
                            </div>

                            <div className={`flex items-start gap-2.5 p-3 rounded-xl border ${reemissao
                                ? 'bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-800/40'
                                : 'bg-red-50 dark:bg-red-900/15 border-red-200 dark:border-red-800/40'}`}
                            >
                                <AlertTriangle size={15} className={`mt-0.5 shrink-0 ${reemissao ? 'text-amber-600' : 'text-red-600'}`} />
                                <p className={`text-sm ${reemissao ? 'text-amber-800 dark:text-amber-300' : 'text-red-800 dark:text-red-300'}`}>
                                    {reemissao
                                        ? 'A declaração anterior fica no histórico e esta passa a valer. Confira se o nome e o CPF/CNPJ estão certos.'
                                        : <><strong>Isso encerra a negociação.</strong> O avalista e o reenvio para análise ficam bloqueados. Se o cliente voltar, será preciso abrir uma negociação nova.</>}
                                </p>
                            </div>
                        </div>

                        <div className="px-5 py-4 border-t border-gray-100 dark:border-slate-800 flex justify-end gap-2 bg-gray-50/50 dark:bg-slate-900/50">
                            <button
                                onClick={() => setConfirmando(false)}
                                disabled={acao !== null}
                                className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmar}
                                disabled={acao !== null}
                                className={`px-4 py-2 rounded-xl text-sm font-bold text-white flex items-center gap-2 disabled:opacity-70 ${reemissao ? 'bg-primary hover:bg-primary/90' : 'bg-red-600 hover:bg-red-700'}`}
                            >
                                {acao === 'emitir' && <Loader2 size={15} className="animate-spin" />}
                                {acao === 'emitir' ? 'Emitindo e assinando…' : reemissao ? 'Emitir nova declaração' : 'Emitir e encerrar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default DeclaracaoNaoAssociacaoCard;
